import React, { useState } from 'react';
import AppShell from '../../components/layout/AppShell';
import { shipmentApi, adminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { fmtPKR, SERVICE_LABELS } from '../../utils/helpers';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const SERVICES = [
  { value: 'DOM_STD', label: 'Domestic Standard (3 days)' },
  { value: 'DOM_EXP', label: 'Domestic Express (1 day)' },
  { value: 'DOM_SD',  label: 'Same Day' },
  { value: 'DOM_ECO', label: 'Economy (5 days)' },
  { value: 'INT_AIR', label: 'International Air' },
  { value: 'INT_SEA', label: 'International Sea' },
  { value: 'INT_EXP', label: 'International Express' },
];

const SPECIAL = ['Fragile', 'Temperature-Controlled', 'Do Not Stack', 'Hazardous-Restricted'];

// 30 major Pakistani cities (sorted alphabetically)
const PK_CITIES = [
  'Abbottabad', 'Bahawalpur', 'Faisalabad', 'Gujranwala', 'Gujrat',
  'Hyderabad', 'Islamabad', 'Jhang', 'Karachi', 'Kasur',
  'Khanewal', 'Larkana', 'Lahore', 'Mardan', 'Mirpur Khas',
  'Multan', 'Muzaffarabad', 'Nawabshah', 'Okara', 'Peshawar',
  'Quetta', 'Rahim Yar Khan', 'Rawalpindi', 'Sahiwal', 'Sargodha',
  'Sheikhupura', 'Sialkot', 'Sukkur', 'Wah Cantt', 'Other',
];

const emptyAddr = {
  contactName: '', contactPhone: '', line1: '',
  city: '', state: '', country: 'PK', pincode: '',
};

export default function BookingPage() {
  const { userType } = useAuth();
  const isStaff = userType === 'employee';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [rate, setRate] = useState(null);
  const [booking, setBooking] = useState(null);
  const [pincodeStatus, setPincodeStatus] = useState({ pickup: null, delivery: null });

  // Two-way address sync state: 'none' | 'deliveryFromPickup' | 'pickupFromDelivery'
  const [addressLink, setAddressLink] = useState('none');

  const [form, setForm] = useState({
    pickupAddress: { ...emptyAddr },
    deliveryAddress: { ...emptyAddr },
    serviceType: 'DOM_STD',
    billingModel: 'PREPAID',
    paymentMethod: 'CASH',
    weightActual: '',
    lengthCm: '', widthCm: '', heightCm: '',
    declaredValue: '0',
    codAmount: '0',
    contentDesc: '',
    specialHandling: [],
    customerName: '', customerMobile: '',
  });

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Address setter - also syncs to the linked side if the toggle is active
  const setAddr = (type, k, v) => {
    setForm(f => {
      const next = { ...f, [type]: { ...f[type], [k]: v } };

      // If editing pickup AND delivery is linked to follow pickup -> mirror it
      if (type === 'pickupAddress' && addressLink === 'deliveryFromPickup') {
        next.deliveryAddress = { ...next.pickupAddress };
      }
      // If editing delivery AND pickup is linked to follow delivery -> mirror it
      if (type === 'deliveryAddress' && addressLink === 'pickupFromDelivery') {
        next.pickupAddress = { ...next.deliveryAddress };
      }
      return next;
    });
  };

  // Toggle the "Same as ..." link on either side
  const toggleLink = (mode) => {
    setAddressLink(prev => {
      // If user clicked the already-active checkbox -> unlink
      if (prev === mode) return 'none';

      // Activate the new link: copy across once
      if (mode === 'deliveryFromPickup') {
        setForm(f => ({ ...f, deliveryAddress: { ...f.pickupAddress } }));
      } else if (mode === 'pickupFromDelivery') {
        setForm(f => ({ ...f, pickupAddress: { ...f.deliveryAddress } }));
      }
      return mode;
    });
  };

  const toggleSpecial = (item) => setF(
    'specialHandling',
    form.specialHandling.includes(item)
      ? form.specialHandling.filter(x => x !== item)
      : [...form.specialHandling, item]
  );

  const checkPincode = async (type, pincode) => {
    if (!pincode || pincode.length < 4) return;
    try {
      const res = await adminApi.checkPincode({ pincode });
      setPincodeStatus(s => ({ ...s, [type]: res.data }));
    } catch {}
  };

  const volWeight = () => {
    const { lengthCm: l, widthCm: w, heightCm: h, serviceType } = form;
    if (!l || !w || !h) return null;
    const div = ['INT_AIR', 'INT_EXP', 'DOM_EXP', 'DOM_SD'].includes(serviceType) ? 5000 : 6000;
    return (parseFloat(l) * parseFloat(w) * parseFloat(h) / div).toFixed(3);
  };

  const chargeableWeight = () => {
    const vw = volWeight();
    const aw = parseFloat(form.weightActual) || 0;
    return vw ? Math.max(aw, parseFloat(vw)).toFixed(3) : aw.toFixed(3);
  };

  const calcRate = async () => {
    if (!form.weightActual) { toast.error('Enter package weight'); return; }
    if (!form.pickupAddress.line1) { toast.error('Enter pickup address'); return; }
    if (!form.deliveryAddress.line1) { toast.error('Enter delivery address'); return; }
    setRateLoading(true);
    try {
      const res = await shipmentApi.calcRate({
        serviceType: form.serviceType,
        weightActual: parseFloat(form.weightActual),
        lengthCm: form.lengthCm ? parseFloat(form.lengthCm) : null,
        widthCm:  form.widthCm  ? parseFloat(form.widthCm)  : null,
        heightCm: form.heightCm ? parseFloat(form.heightCm) : null,
        declaredValue: parseFloat(form.declaredValue || 0),
        codAmount:     parseFloat(form.codAmount || 0),
      });
      if (res.success) { setRate(res.data); setStep(2); }
      else throw new Error(res.message);
    } catch (e) { toast.error(e.message || 'Rate calculation failed'); }
    finally { setRateLoading(false); }
  };

  const confirmBooking = async () => {
    setLoading(true);
    try {
      const payload = {
        serviceType: form.serviceType,
        billingModel: form.billingModel,
        paymentMethod: form.paymentMethod,
        weightActual: parseFloat(form.weightActual),
        lengthCm: form.lengthCm ? parseFloat(form.lengthCm) : null,
        widthCm:  form.widthCm  ? parseFloat(form.widthCm)  : null,
        heightCm: form.heightCm ? parseFloat(form.heightCm) : null,
        declaredValue: parseFloat(form.declaredValue || 0),
        codAmount: parseFloat(form.codAmount || 0),
        contentDesc: form.contentDesc,
        specialHandling: form.specialHandling,
        pickupAddress: form.pickupAddress,
        deliveryAddress: form.deliveryAddress,
      };
      if (isStaff) {
        payload.customerName   = form.customerName;
        payload.customerMobile = form.customerMobile;
      }
      const res = await shipmentApi.create(payload);
      if (res.success) {
        setBooking(res.data);
        setStep(3);
        toast.success('Shipment booked!');
      } else throw new Error(res.message);
    } catch (e) { toast.error(e.message || 'Booking failed'); }
    finally { setLoading(false); }
  };

  // -- Step 3: Success screen ------------------------------------------------
  if (step === 3 && booking) {
    return (
      <AppShell title="Booking Confirmed">
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>{'\u2713'}</div>
              <h2 style={{ color: 'var(--navy)', marginBottom: 8 }}>Shipment Booked!</h2>
              <div style={{
                fontFamily: 'monospace', fontSize: '1.4rem', fontWeight: 700,
                color: 'var(--amber)', margin: '12px 0',
              }}>
                {booking.tracking_id || booking.trackingId}
              </div>

              <div style={{
                background: 'var(--cream-warm, #F1ECDF)',
                borderRadius: 8, padding: 16,
                margin: '20px 0', textAlign: 'left',
              }}>
                <RateLine label="Base Freight"   v={booking.charges?.baseFreight} />
                <RateLine label="Fuel Surcharge" v={booking.charges?.fuelSurcharge} />
                <RateLine label="COD Fee"        v={booking.charges?.codFee} />
                <RateLine label="Insurance"      v={booking.charges?.insuranceFee} />
                <RateLine label="Tax (GST)"      v={booking.charges?.taxAmount} />
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  paddingTop: 8, borderTop: '2px solid var(--navy)',
                }}>
                  <span style={{ fontWeight: 700 }}>Total</span>
                  <span style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--navy)' }}>
                    {fmtPKR(booking.charges?.totalCharges)}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: '.85rem', color: 'var(--muted)', marginBottom: 24 }}>
                Chargeable weight: <strong>{booking.charges?.chargeableWeight} kg</strong>
                {booking.charges?.volumetricWeight > 0 &&
                  ` (Volumetric: ${booking.charges.volumetricWeight} kg)`}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link
                  to={`/track/${booking.tracking_id || booking.trackingId}`}
                  className="btn btn-primary"
                >
                  Track Shipment
                </Link>
                <button className="btn btn-amber" onClick={() => {
                  setStep(1); setRate(null); setBooking(null);
                  setAddressLink('none');
                  setForm({
                    pickupAddress: { ...emptyAddr }, deliveryAddress: { ...emptyAddr },
                    serviceType: 'DOM_STD', billingModel: 'PREPAID', paymentMethod: 'CASH',
                    weightActual: '', lengthCm: '', widthCm: '', heightCm: '',
                    declaredValue: '0', codAmount: '0', contentDesc: '', specialHandling: [],
                    customerName: '', customerMobile: '',
                  });
                }}>Book Another</button>
                <button className="btn btn-outline" onClick={() => {
                  const tid = booking.tracking_id || booking.trackingId;
                  const label = `TRACKING: ${tid}\nService: ${SERVICE_LABELS[form.serviceType]}\nWeight: ${form.weightActual}kg\nFrom: ${form.pickupAddress.city}\nTo: ${form.deliveryAddress.city}\nTotal: PKR ${booking.charges?.totalCharges}`;
                  const w = window.open('', '_blank');
                  w.document.write(`<pre style="font-family:monospace;font-size:12px;padding:20px">${label}</pre>`);
                  w.print();
                }}>Print Label</button>
              </div>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Book Shipment">
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          {['Shipment Details', 'Review & Confirm'].map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: step > i + 1 ? 'var(--success)' : step === i + 1 ? 'var(--amber)' : 'var(--line)',
                  color: step >= i + 1 ? '#fff' : 'var(--muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '.8rem', fontWeight: 700, flexShrink: 0,
                }}>
                  {step > i + 1 ? '\u2713' : i + 1}
                </div>
                <span style={{
                  fontSize: '.85rem',
                  fontWeight: step === i + 1 ? 600 : 400,
                  color: step === i + 1 ? 'var(--navy)' : 'var(--muted)',
                }}>{s}</span>
              </div>
              {i < 1 && <div style={{
                flex: 1, height: 2,
                background: step > 1 ? 'var(--amber)' : 'var(--line)',
                borderRadius: 2,
              }} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div style={{ display: 'grid', gap: 16 }}>
            {isStaff && (
              <div className="card">
                <div className="card-header"><span className="card-title">Walk-in Customer</span></div>
                <div className="card-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Customer Name <span className="req">*</span></label>
                      <input className="form-control" placeholder="Muhammad Ali"
                        value={form.customerName}
                        onChange={e => setF('customerName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Mobile <span className="req">*</span></label>
                      <input className="form-control" placeholder="+923001234567"
                        value={form.customerMobile}
                        onChange={e => setF('customerMobile', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Addresses with two-way Same-As link */}
            <div className="form-row">
              <AddressCard
                title="Pickup Address"
                addr={form.pickupAddress}
                onChange={(k, v) => setAddr('pickupAddress', k, v)}
                onPincodeBlur={p => checkPincode('pickup', p)}
                pincodeStatus={pincodeStatus.pickup}
                sameAsLabel="Same as Delivery Address"
                sameAsChecked={addressLink === 'pickupFromDelivery'}
                onSameAsToggle={() => toggleLink('pickupFromDelivery')}
                locked={addressLink === 'pickupFromDelivery'}
              />
              <AddressCard
                title="Delivery Address"
                addr={form.deliveryAddress}
                onChange={(k, v) => setAddr('deliveryAddress', k, v)}
                onPincodeBlur={p => checkPincode('delivery', p)}
                pincodeStatus={pincodeStatus.delivery}
                sameAsLabel="Same as Pickup Address"
                sameAsChecked={addressLink === 'deliveryFromPickup'}
                onSameAsToggle={() => toggleLink('deliveryFromPickup')}
                locked={addressLink === 'deliveryFromPickup'}
              />
            </div>

            {/* Service & billing */}
            <div className="card">
              <div className="card-header"><span className="card-title">Service & Billing</span></div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Service Type <span className="req">*</span></label>
                    <select className="form-control" value={form.serviceType}
                            onChange={e => setF('serviceType', e.target.value)}>
                      {SERVICES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Billing Model</label>
                    <select className="form-control" value={form.billingModel}
                            onChange={e => setF('billingModel', e.target.value)}>
                      <option value="PREPAID">Prepaid</option>
                      <option value="COD">Cash on Delivery (COD)</option>
                      <option value="POSTPAID">Postpaid (Credit)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select className="form-control" value={form.paymentMethod}
                            onChange={e => setF('paymentMethod', e.target.value)}>
                      <option value="CASH">Cash</option>
                      <option value="CARD">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="WALLET">Wallet</option>
                      <option value="NET_BANKING">Net Banking</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Package details */}
            <div className="card">
              <div className="card-header"><span className="card-title">Package Details</span></div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Actual Weight (kg) <span className="req">*</span></label>
                    <input className="form-control" type="number" step="0.001" min="0.001"
                      placeholder="0.500" value={form.weightActual}
                      onChange={e => setF('weightActual', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Dimensions (cm) {'\u2014'} L x W x H</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-control" type="number" placeholder="L"
                        value={form.lengthCm} onChange={e => setF('lengthCm', e.target.value)} />
                      <input className="form-control" type="number" placeholder="W"
                        value={form.widthCm} onChange={e => setF('widthCm', e.target.value)} />
                      <input className="form-control" type="number" placeholder="H"
                        value={form.heightCm} onChange={e => setF('heightCm', e.target.value)} />
                    </div>
                  </div>
                </div>

                {volWeight() && (
                  <div style={{
                    padding: '8px 12px', background: 'var(--cream-warm)',
                    borderRadius: 8, marginBottom: 12, fontSize: '.88rem',
                  }}>
                    Volumetric weight: <strong>{volWeight()} kg</strong>
                    {' | '}Chargeable: <strong>{chargeableWeight()} kg</strong>
                    {parseFloat(volWeight()) > parseFloat(form.weightActual || 0) &&
                      <span style={{ color: 'var(--warning)', marginLeft: 6 }}>
                        Volumetric weight applies
                      </span>}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Declared Value (PKR)</label>
                    <input className="form-control" type="number" min="0"
                      value={form.declaredValue}
                      onChange={e => setF('declaredValue', e.target.value)} />
                  </div>
                  {form.billingModel === 'COD' && (
                    <div className="form-group">
                      <label className="form-label">COD Amount (PKR) <span className="req">*</span></label>
                      <input className="form-control" type="number" min="0"
                        value={form.codAmount}
                        onChange={e => setF('codAmount', e.target.value)} />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Content Description</label>
                  <input className="form-control" placeholder="e.g. Clothing, Documents, Electronics"
                    value={form.contentDesc}
                    onChange={e => setF('contentDesc', e.target.value)} />
                </div>

                <div className="form-group">
                  <label className="form-label">Special Handling</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {SPECIAL.map(item => (
                      <label key={item} style={{
                        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                        padding: '5px 12px', borderRadius: 6, fontSize: '.82rem',
                        border: `1.5px solid ${form.specialHandling.includes(item) ? 'var(--navy)' : 'var(--line)'}`,
                        background: form.specialHandling.includes(item) ? 'var(--navy)' : 'var(--paper)',
                        color: form.specialHandling.includes(item) ? '#fff' : 'var(--ink)',
                        transition: 'all 0.15s',
                      }}>
                        <input type="checkbox" style={{ display: 'none' }}
                          checked={form.specialHandling.includes(item)}
                          onChange={() => toggleSpecial(item)} />
                        {form.specialHandling.includes(item) ? '\u2713 ' : ''}{item}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button className="btn btn-primary btn-lg w-full"
                    onClick={calcRate} disabled={rateLoading}>
              {rateLoading ? 'Calculating rate...' : `Calculate Rate & Review ${'\u2192'}`}
            </button>
          </div>
        )}

        {step === 2 && rate && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Rate Breakdown</span></div>
              <div className="card-body">
                <RateRow label="Base Freight"    v={rate.baseFreight} />
                <RateRow label="Fuel Surcharge"  v={rate.fuelSurcharge} />
                <RateRow label="Insurance (1%)"  v={rate.insuranceFee} />
                <RateRow label="COD Fee"         v={rate.codFee} />
                <RateRow label="Tax / GST (18%)" v={rate.taxAmount} />
                <div style={{ borderTop: '2px solid var(--navy)', paddingTop: 12, marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>Total Charges</span>
                    <span style={{ fontWeight: 700, fontSize: '1.2rem', color: 'var(--navy)' }}>
                      {fmtPKR(rate.totalCharges)}
                    </span>
                  </div>
                </div>
                <div style={{
                  marginTop: 12, padding: 10, background: 'var(--cream-warm)',
                  borderRadius: 6, fontSize: '.8rem', color: 'var(--muted)',
                }}>
                  Actual: {form.weightActual} kg | Volumetric: {rate.volumetricWeight || 0} kg |
                  Chargeable: <strong>{rate.chargeableWeight} kg</strong>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Shipment Summary</span></div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <SumItem label="From" v={`${form.pickupAddress.line1}, ${form.pickupAddress.city}`} />
                  <SumItem label="To"   v={`${form.deliveryAddress.line1}, ${form.deliveryAddress.city}`} />
                  <SumItem label="Service"  v={SERVICES.find(s => s.value === form.serviceType)?.label} />
                  <SumItem label="Billing"  v={form.billingModel} />
                  <SumItem label="Payment"  v={form.paymentMethod} />
                  <SumItem label="Contents" v={form.contentDesc || '\u2014'} />
                  {form.codAmount > 0 && <SumItem label="COD Amount" v={fmtPKR(form.codAmount)} />}
                  {form.specialHandling.length > 0 &&
                    <SumItem label="Special" v={form.specialHandling.join(', ')} />}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" onClick={() => setStep(1)}>
                {'\u2190'} Edit Details
              </button>
              <button className="btn btn-amber btn-lg" style={{ flex: 1 }}
                      onClick={confirmBooking} disabled={loading}>
                {loading ? 'Confirming...' : `Confirm Booking ${'\u2014'} ${fmtPKR(rate.totalCharges)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

// --------------------------------------------------------------------------
// AddressCard: now supports "Same as ..." checkbox and locked read-only mode
// --------------------------------------------------------------------------
function AddressCard({
  title, addr, onChange,
  onPincodeBlur, pincodeStatus,
  sameAsLabel, sameAsChecked, onSameAsToggle, locked,
}) {
  const lockedStyle = locked ? {
    background: 'var(--cream-warm)',
    cursor: 'not-allowed',
    color: 'var(--muted)',
  } : {};

  return (
    <div className="card">
      <div className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
        <span className="card-title">{title}</span>
        {sameAsLabel && (
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer', fontSize: '.82rem',
            color: sameAsChecked ? 'var(--navy)' : 'var(--muted)',
            fontWeight: sameAsChecked ? 600 : 500,
          }}>
            <input
              type="checkbox"
              checked={!!sameAsChecked}
              onChange={onSameAsToggle}
              style={{ accentColor: 'var(--navy)' }}
            />
            {sameAsLabel}
          </label>
        )}
      </div>
      <div className="card-body">
        <div className="form-group">
          <label className="form-label">Contact Name</label>
          <input className="form-control" placeholder="Full name"
                 style={lockedStyle} disabled={locked}
                 value={addr.contactName}
                 onChange={e => onChange('contactName', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Contact Phone</label>
          <input className="form-control" placeholder="+92 300 000 0000"
                 style={lockedStyle} disabled={locked}
                 value={addr.contactPhone}
                 onChange={e => onChange('contactPhone', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Street Address <span className="req">*</span></label>
          <input className="form-control" placeholder="House/Plot, Street, Area"
                 style={lockedStyle} disabled={locked}
                 value={addr.line1}
                 onChange={e => onChange('line1', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">City <span className="req">*</span></label>
            <select className="form-control"
                    style={lockedStyle} disabled={locked}
                    value={addr.city || ''}
                    onChange={e => onChange('city', e.target.value)}>
              <option value="">Select a city</option>
              {PK_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Pincode</label>
            <input className="form-control" placeholder="75500"
                   style={lockedStyle} disabled={locked}
                   value={addr.pincode}
                   onChange={e => onChange('pincode', e.target.value)}
                   onBlur={e => onPincodeBlur(e.target.value)} />
          </div>
        </div>
        {pincodeStatus && !locked && (
          <div style={{
            padding: '6px 10px', fontSize: '.78rem', borderRadius: 6,
            background: pincodeStatus.serviceable ? 'var(--success-bg, #D1FAE5)' : 'var(--danger-bg, #FEE2E2)',
            color: pincodeStatus.serviceable ? '#047857' : '#B91C1C',
            marginBottom: 0,
          }}>
            {pincodeStatus.serviceable
              ? `Serviceable - ${pincodeStatus.zone}${pincodeStatus.isRemote ? ' (Remote area surcharge applies)' : ''}`
              : (pincodeStatus.message || 'Not serviceable')}
          </div>
        )}
      </div>
    </div>
  );
}

function RateLine({ label, v }) {
  if (!v || v === 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ fontSize: '.85rem', color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontSize: '.85rem' }}>{fmtPKR(v)}</span>
    </div>
  );
}

function RateRow({ label, v }) {
  if (!v || v === 0) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      padding: '7px 0', borderBottom: '1px solid var(--line-soft, #F0EDE6)',
    }}>
      <span style={{ fontSize: '.875rem', color: 'var(--ink-soft)' }}>{label}</span>
      <span style={{ fontSize: '.875rem', fontWeight: 500 }}>{fmtPKR(v)}</span>
    </div>
  );
}

function SumItem({ label, v }) {
  return (
    <div>
      <div style={{
        fontSize: '.7rem', color: 'var(--muted)',
        textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2,
      }}>{label}</div>
      <div style={{ fontSize: '.875rem', fontWeight: 500 }}>{v}</div>
    </div>
  );
}
